import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";

@customElement("create-tenant-dialog")
export class CreateTenantDialog extends LitElement {
  static styles = css`
    :host {
      display: block;
      font-family: Geist, sans-serif;
    }

    .form-field {
      margin-bottom: var(--spacing-16, 16px);
    }

    .form-field label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-4, 4px);
    }

    .form-field .help {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-top: var(--spacing-4, 4px);
    }

    .modal-footer {
      display: flex;
      gap: var(--spacing-8, 8px);
      justify-content: flex-end;
    }

    .success-message {
      text-align: center;
      padding: var(--spacing-16, 16px) 0;
    }

    bee-alert {
      margin-bottom: var(--spacing-16, 16px);
    }
  `;

  @property({ type: Boolean }) open = false;

  @state() private _name = "";
  @state() private _applying = false;
  @state() private _error = "";

  private get _isValid(): boolean {
    return /^[a-z0-9][a-z0-9-]*$/.test(this._name);
  }

  private _close() {
    this._name = "";
    this._error = "";
    this._applying = false;
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _submit() {
    if (!this._isValid || this._applying) return;
    this._applying = true;
    this._error = "";
    try {
      await api.createTenant({ name: this._name });
      this.dispatchEvent(new CustomEvent("tenant-created", { detail: { name: this._name } }));
      this._close();
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to create tenant";
    } finally {
      this._applying = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <bee-modal open in-root title="Add Tenant" @bee-request-close=${this._close}>
        <div class="form-field">
          <label>Tenant Name</label>
          <bee-input
            placeholder="e.g., acme-corp"
            .value=${this._name}
            @bee-input=${(e: Event) => { this._name = (e.target as HTMLInputElement).value.trim().toLowerCase(); }}
          ></bee-input>
          <div class="help">Lowercase alphanumeric with hyphens.</div>
        </div>

        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button
            label=${this._applying ? "Creating..." : "Add Tenant"}
            bee-type="primary"
            @click=${this._submit}
            ?disabled=${!this._isValid || this._applying}
            ?loading=${this._applying}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
